import React, { useEffect, useState } from 'react'
import { withRouter } from 'react-router-dom'
import { toast } from 'react-toastify'
import { connect } from 'react-redux'
import cn from 'classnames'
import SegmentedControlV2 from '../../components/SegmentedControl/SegmentedControl'
import GratuityHeader from '../../components/GratuityHeader'
import BackButtonChevron from '../../components/BackButtonChevron'
import Button from '../../components/Button'
import NumberPadV2 from '../../components/NumberPad/NumberPadV2'
import SelectionGroup from './SelectionGroup'
import PaymentsList from './containers/PaymentsList'
import { ReactComponent as LoadingIcon } from '../../assets/loading.svg'

import makeTenderSelections, {
  tenderSelection as tenderSelectionFactory
} from './utils/makeTenderSelections'
import { makeGetOrder } from '../../selectors/order'
import { centsToDollars } from '../../utils/formatters'
import useNumberPad from '../../hooks/useNumberPad'
import { TabOrderDetailsViewModel } from '../../viewmodels/TabOrderViewModel'
import useCloseOrder from './hooks/useCloseOrder'
import useUpdatePayment from '../../hooks/useUpdatePayment'
import { numberAsCurrency } from '../../utils/orderUtils'

import styles from './index.module.scss'

const splitTenderChoices = [
  { label: 'Evenly', value: 'evenly' },
  { label: 'Percent', value: 'percent' },
  { label: 'Custom', value: 'custom' }
]

const Tender = ({
  orderId,
  paymentId,
  paymentBeingUpdated,
  totalAmountInCents,
  effectiveBalanceDueInCents,
  totalDisplay,
  balanceDueDisplay,
  onConfirm,
  onCancel,
  onGoBack,
  className
}) => {
  const [shouldAnimatePop, setShouldAnimatePop] = useState(false)
  const [shouldAnimateSlide, setShouldAnimateSlide] = useState(false)
  const [selectedTenderType, setSelectedTenderType] = useState('evenly')
  const [selectedTenderAmount, setSelectedTenderAmount] = useState(undefined)
  const [selectionItemsEvenly, setSelectionItemsEvenly] = useState(
    makeTenderSelections('evenly', totalAmountInCents, effectiveBalanceDueInCents)
  )
  const [selectionItemsPercent, setSelectionItemsPercent] = useState(
    makeTenderSelections('percent', totalAmountInCents)
  )
  const [closeOrder] = useCloseOrder(orderId)
  const [updatePayment, updatePaymentStates] = useUpdatePayment()
  const selectionItemsCustom = []
  const customEntry = selectedTenderType === 'custom'
  const mediaQueryTablet = window.matchMedia('(max-width: 1920px)')
  const mediaQueryMobile = window.matchMedia('(max-width: 360px)')

  const [customOutput, onCustomInput, customRawValue, customIsValid] = useNumberPad(
    0,
    centsToDollars,
    (value) => parseInt(value) <= effectiveBalanceDueInCents && parseInt(value) > 0,
    6
  )

  useEffect(() => {
    setSelectionItemsEvenly(
      makeTenderSelections('evenly', totalAmountInCents, effectiveBalanceDueInCents)
    )
    setSelectionItemsPercent(
      makeTenderSelections('percent', totalAmountInCents, effectiveBalanceDueInCents)
    )
  }, [totalAmountInCents, effectiveBalanceDueInCents])

  useEffect(() => {
    const tip = paymentBeingUpdated?.tip ?? 0
    const amountInCents = paymentBeingUpdated?.amountInCents ?? 0
    const subtotalInCents = amountInCents - tip
    setSelectedTenderAmount(
      tenderSelectionFactory(undefined, centsToDollars(subtotalInCents), subtotalInCents, false)
    )
    onCustomInput(subtotalInCents, true)
  }, [paymentId, paymentBeingUpdated])

  const selectionItems = {
    evenly: selectionItemsEvenly,
    percent: selectionItemsPercent,
    custom: selectionItemsCustom
  }
  const selectionGroupItems = selectionItems[selectedTenderType]

  useEffect(() => {
    if (updatePaymentStates.success) {
      if (customEntry) {
        onConfirm(customRawValue, paymentBeingUpdated?.paymentType === 'cash')
      } else {
        onConfirm(selectedTenderAmount?.amountInCents, paymentBeingUpdated?.paymentType === 'cash')
      }
    }
  }, [updatePaymentStates.success])

  const primaryText = customEntry ? customOutput : selectedTenderAmount?.text ?? centsToDollars(0)
  const secondaryText = !mediaQueryMobile?.matches
    ? `Total: ${totalDisplay} • Balance Due: ${balanceDueDisplay}`
    : `Total: ${totalDisplay}\nBalance Due: ${balanceDueDisplay}`
  const noBalanceDue = effectiveBalanceDueInCents === 0 && !paymentId

  const triggerPopAnimation = () => {
    setShouldAnimatePop(true)
    setTimeout(() => setShouldAnimatePop(false), 100)
  }

  const handleTenderTypeDidChange = (value) => {
    if (selectedTenderType !== value && !shouldAnimateSlide) {
      setShouldAnimateSlide(true)
    }

    setTimeout(() => {
      onCustomInput(0, true)
      setSelectedTenderType(value)
      setSelectedTenderAmount(undefined)
      setShouldAnimateSlide(false)
    }, 100)
  }

  const handleTenderAmountDidChange = (item, index) => {
    setSelectedTenderAmount({ ...item, index })

    if (selectedTenderAmount?.index !== index && !shouldAnimatePop) {
      triggerPopAnimation()
    }
  }

  const handleNewPaymentConfirmed = () => {
    if (noBalanceDue) {
      closeOrder(orderId)
    } else if (customEntry) {
      if (customIsValid) {
        onConfirm(customRawValue)
      } else {
        toast.error('Please enter a valid amount before proceeding')
      }
    } else if (selectedTenderAmount?.amountInCents > 0) {
      onConfirm(selectedTenderAmount?.amountInCents)
    } else {
      toast.error('Please enter a valid amount before proceeding')
    }
  }

  const handleUpdatePaymentConfirmed = () => {
    if (noBalanceDue) {
      closeOrder(orderId)
    } else if (customEntry) {
      if (customIsValid) {
        updatePayment({
          ...paymentBeingUpdated,
          orderId,
          paymentId,
          amountInCents: customRawValue,
          tip: 0
        })
      } else {
        toast.error('Please enter a valid amount before proceeding')
      }
    } else if (selectedTenderAmount?.amountInCents > 0) {
      updatePayment({
        ...paymentBeingUpdated,
        orderId,
        paymentId,
        amountInCents: selectedTenderAmount?.amountInCents,
        tip: 0
      })
    } else {
      toast.error('Please enter a valid amount before proceeding')
    }
  }

  const handleOnConfirm = () => {
    if (paymentId !== undefined) {
      handleUpdatePaymentConfirmed()
    } else {
      handleNewPaymentConfirmed()
    }
  }

  let confirmButtonText = 'Confirm'

  if (noBalanceDue) {
    confirmButtonText = 'Close Order'
  } else if (updatePaymentStates.loading) {
    confirmButtonText = (
      <>
        Updating <LoadingIcon className={styles.loadingIcon} />
      </>
    )
  }

  return (
    <div className={cn(styles.SplitTender, className)}>
      <div className={styles.goBack}>
        <BackButtonChevron
          onClick={onGoBack}
          backButtonText={mediaQueryMobile?.matches ? '' : 'Go Back'}
        />
        <span className={styles.mobileTitle}>Split Tender</span>
        <span></span>
      </div>
      <div className={styles.SplitTender__header}>
        <GratuityHeader
          className={cn(styles.gratuityHeader, { [styles.pop]: shouldAnimatePop })}
          primaryText={primaryText}
          secondaryText={secondaryText}
          isSplitTender={true}
          isMobileView={mediaQueryTablet?.matches || mediaQueryMobile?.matches}
        />
      </div>
      <div className={styles.SplitTender__splitChoices}>
        <SegmentedControlV2
          className={styles.segmentedControl}
          items={splitTenderChoices}
          selectedValue={selectedTenderType}
          onSelect={handleTenderTypeDidChange}
          disabled={noBalanceDue}
        />
      </div>
      <div className={styles.SplitTender__body}>
        <div className={styles.paymentList}>
          <PaymentsList dialogClassName={styles.dialog} />
        </div>
        <div className={cn(styles.splitChoicesContent, { [styles.slide]: shouldAnimateSlide })}>
          <SelectionGroup
            className={cn(styles.selectionGroup, { [styles.isHidden]: customEntry })}
            items={selectionGroupItems}
            selectedIndex={selectedTenderAmount?.index}
            onChange={handleTenderAmountDidChange}
          />
          <NumberPadV2
            className={cn(styles.numberPad, { [styles.isHidden]: !customEntry })}
            backspace
            disabled={noBalanceDue}
            onTapButton={onCustomInput}
          />
        </div>
        <div className={styles.actionButtons}>
          <Button className={cn(styles.button, styles.confirm)} onClick={handleOnConfirm}>
            {confirmButtonText}
          </Button>
          <Button className={styles.button} onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = (state, props) => {
  const orderId = props?.match?.params?.orderId
  const paymentId = props?.match?.params?.paymentId
  const getOrder = makeGetOrder()
  const order = TabOrderDetailsViewModel(getOrder(state, orderId))
  const balanceDueInCents = order.isMultiPayment
    ? order.remainingBalanceInCents
    : order.totalAmountInCents
  const paymentBeingUpdated = order.payments?.find?.((payment) => payment.uuid === paymentId)
  const balanceDueDisplay = numberAsCurrency(balanceDueInCents / 100.0)
  const tip = paymentBeingUpdated?.tip ?? 0
  const amountInCents = paymentBeingUpdated?.amountInCents ?? 0
  const subtotalInCents = amountInCents - tip
  const effectiveBalanceDueInCents = balanceDueInCents + subtotalInCents

  return {
    orderId,
    paymentId,
    paymentBeingUpdated,
    totalAmountInCents: order.totalAmountInCents,
    balanceDueInCents: balanceDueInCents,
    totalDisplay: order.amount,
    balanceDueDisplay: balanceDueDisplay,
    effectiveBalanceDueInCents
  }
}

const mapDispatchToProps = (dispatch, props) => {
  const orderId = props?.match?.params?.orderId
  const paymentId = props?.match?.params?.paymentId
  const push = props?.history?.push ?? function () {}
  const replace = props?.history?.replace ?? function () {}
  const goBack = props?.history?.goBack ?? function () {}

  return {
    onConfirm: (tenderAmountInCents, isCash = false) => {
      if (paymentId !== undefined) {
        let pathname = `/tabs/${orderId}/close-tender/${paymentId}`
        if (isCash) pathname = `/change-calculator/${orderId}?tab`

        push({
          pathname,
          state: { tenderAmountInCents, isTenderUpdate: true }
        })
      } else {
        push({
          pathname: `/tabs/${orderId}/close-tender`,
          state: { tenderAmountInCents }
        })
      }
    },
    onGoBack: () => goBack(),
    onCancel: () => {
      if (paymentId !== undefined) {
        replace(`/tender/${orderId}`)
      } else {
        goBack()
      }
    }
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Tender))
